import { z } from 'zod'
import { SurveyQualityReportData } from './survey-quality'
import { FirestoreTimestamp } from './utils'

// ============================================================================
// Maintenance Completion Report Data Types
// ============================================================================

/**
 * Filter reference for applied filters display
 */
export const FilterReference = z.object({
  id: z.string(),
  name: z.string(),
})
export type FilterReference = z.infer<typeof FilterReference>

/**
 * Applied filters summary for display in reports
 */
export const AppliedFilters = z.object({
  dateRange: z.object({
    startDate: z.string(),
    endDate: z.string(),
  }),
  sites: z.array(FilterReference).nullable(),
  customerOrganizations: z.array(FilterReference).nullable(),
  workRoutes: z.array(FilterReference).nullable(),
  mechanics: z.array(FilterReference).nullable(),
})
export type AppliedFilters = z.infer<typeof AppliedFilters>

/**
 * Summary metrics for maintenance completion report
 */
export const SummaryMetrics = z.object({
  totalTasks: z.number(),
  completedTasks: z.number(),
  completionRate: z.number(),
  totalHours: z.number(),
  completedHours: z.number(),
  hoursCompletionRate: z.number(),
  totalSites: z.number(),
  totalUnits: z.number(),
  totalMechanics: z.number(),
  appliedFilters: AppliedFilters,
})
export type SummaryMetrics = z.infer<typeof SummaryMetrics>

/**
 * Site completion data for maintenance completion report
 */
export const SiteCompletionData = z.object({
  siteId: z.string(),
  siteName: z.string(),
  customerOrganizationId: z.string().nullable().optional(),
  customerOrganizationName: z.string().optional(),
  totalTasks: z.number(),
  completedTasks: z.number(),
  completionRate: z.number(),
  unitCount: z.number(),
})
export type SiteCompletionData = z.infer<typeof SiteCompletionData>

/**
 * Customer organization completion data for maintenance completion report
 */
export const CustomerCompletionData = z.object({
  customerOrganizationId: z.string(),
  name: z.string(),
  totalTasks: z.number(),
  completedTasks: z.number(),
  completionRate: z.number(),
  siteCount: z.number(),
})
export type CustomerCompletionData = z.infer<typeof CustomerCompletionData>

/**
 * Work route completion data for maintenance completion report
 */
export const WorkRouteCompletionData = z.object({
  workRouteId: z.string(),
  name: z.string(),
  totalTasks: z.number(),
  completedTasks: z.number(),
  completionRate: z.number(),
  siteCount: z.number(),
})
export type WorkRouteCompletionData = z.infer<typeof WorkRouteCompletionData>

/**
 * Incomplete unit detail for mechanic performance
 */
export const IncompleteUnit = z.object({
  unitId: z.string(),
  buildingDesignation: z.string(),
  siteName: z.string(),
  totalTasks: z.number(),
  completedTasks: z.number(),
  completionRate: z.number(),
})
export type IncompleteUnit = z.infer<typeof IncompleteUnit>

/**
 * Mechanic performance data for maintenance completion report
 */
export const MechanicPerformanceData = z.object({
  mechanicId: z.string(),
  name: z.string(),
  // Email is optional since legacy mechanics may not have user records
  email: z.string().optional(),
  totalTasksAssigned: z.number(),
  tasksCompleted: z.number(),
  completionRate: z.number(),
  // Total minutes spent on completed tasks
  totalMinutes: z.number(),
  unitsServiced: z.number(),
  incompleteUnits: z.array(IncompleteUnit),
})
export type MechanicPerformanceData = z.infer<typeof MechanicPerformanceData>

/**
 * Task detail for unit in maintenance completion report
 */
export const TaskDetail = z.object({
  activityId: z.string(),
  type: z.enum(['maintenance', 'callback', 'test', 'repair']),
  status: z.enum(['completed', 'pending', 'overdue', 'skipped']),
  description: z.string().optional(),
  scheduledDate: z.string().optional(),
  completedDate: z.string().optional(),
  completedBy: z.string().nullable().optional(),
  mechanicName: z.string().optional(),
  minutes: z.number().optional(),
})
export type TaskDetail = z.infer<typeof TaskDetail>

/**
 * Unit detail data for maintenance completion report
 */
export const UnitDetailData = z.object({
  unitId: z.string(),
  buildingDesignation: z.string(),
  manufacturer: z.string(),
  type: z.string(),
  siteId: z.string(),
  siteName: z.string(),
  customerOrganizationId: z.string().nullable().optional(),
  totalTasks: z.number(),
  completedTasks: z.number(),
  completionRate: z.number(),
  tasks: z.array(TaskDetail),
})
export type UnitDetailData = z.infer<typeof UnitDetailData>

/**
 * Complete report data structure for maintenance completion reports
 */
export const MaintenanceCompletionReportData = z.object({
  summary: SummaryMetrics,
  siteCompletion: z.array(SiteCompletionData),
  customerCompletion: z.array(CustomerCompletionData),
  workRouteCompletion: z.array(WorkRouteCompletionData),
  mechanicPerformance: z.array(MechanicPerformanceData),
  unitDetails: z.array(UnitDetailData),
})
export type MaintenanceCompletionReportData = z.infer<
  typeof MaintenanceCompletionReportData
>

// ============================================================================
// Asset Performance Report Data Types
// ============================================================================

/**
 * Callback detail data for asset performance report CSV export
 * Includes all fields collected in the Callback form + Unit Name + Site Name + Site Address
 */
export const CallbackDetailData = z.object({
  date: z.string(),
  timeArrived: z.string().optional(),
  timeDeparted: z.string().optional(),
  entrapment: z.boolean(),
  runningOnArrival: z.boolean(),
  problemType: z.string(),
  problemDescription: z.string(),
  workPerformed: z.string(),
  workOrder: z.string().nullable().optional(),
  completedBy: z.string().optional(),
  ahjId: z.string().optional(),
  unitName: z.string(),
  siteName: z.string(),
  siteAddress: z.string(),
})
export type CallbackDetailData = z.infer<typeof CallbackDetailData>

/**
 * Unit performance data for asset performance report
 */
export const UnitPerformanceData = z.object({
  unitId: z.string(),
  buildingDesignation: z.string(),
  manufacturer: z.string(),
  type: z.string(),
  siteId: z.string(),
  siteName: z.string(),
  customerOrganizationId: z.string().nullable().optional(),
  customerOrganizationName: z.string().optional(),
  totalTasks: z.number(),
  completedTasks: z.number(),
  completionRate: z.number(),
  totalCallbacks: z.number(),
})
export type UnitPerformanceData = z.infer<typeof UnitPerformanceData>

/**
 * Property/Site performance data for asset performance report
 */
export const PropertyPerformanceData = z.object({
  siteId: z.string(),
  siteName: z.string(),
  customerOrganizationId: z.string().nullable().optional(),
  customerOrganizationName: z.string().optional(),
  region: z.string().optional(),
  unitCount: z.number(),
  totalTasks: z.number(),
  completedTasks: z.number(),
  completionRate: z.number(),
  totalCallbacks: z.number(),
  avgCallbacksPerUnit: z.number(),
})
export type PropertyPerformanceData = z.infer<typeof PropertyPerformanceData>

/**
 * Time-series data point for asset performance tracking
 */
export const AssetPerformanceTimeSeriesPoint = z.object({
  date: z.string(), // ISO date string
  totalTasks: z.number(),
  completedTasks: z.number(),
  completionRate: z.number(),
  callbacks: z.number(),
})
export type AssetPerformanceTimeSeriesPoint = z.infer<
  typeof AssetPerformanceTimeSeriesPoint
>

/**
 * Summary metrics for asset performance report
 */
export const AssetPerformanceSummary = z.object({
  totalTasks: z.number(),
  completedTasks: z.number(),
  completionRate: z.number(),
  totalUnits: z.number(),
  totalSites: z.number(),
  totalCallbacks: z.number(),
  avgCallbacksPerUnit: z.number(),
  appliedFilters: AppliedFilters,
})
export type AssetPerformanceSummary = z.infer<typeof AssetPerformanceSummary>

/**
 * Complete report data structure for asset performance reports
 */
export const AssetPerformanceReportData = z.object({
  summary: AssetPerformanceSummary,
  propertyPerformance: z.array(PropertyPerformanceData),
  unitPerformance: z.array(UnitPerformanceData),
  timeSeriesData: z.array(AssetPerformanceTimeSeriesPoint),
  callbackDetails: z.array(CallbackDetailData).default([]),
})
export type AssetPerformanceReportData = z.infer<
  typeof AssetPerformanceReportData
>

/**
 * Type guard to check if report data is AssetPerformanceReportData
 */
export function isAssetPerformanceReportData(
  data: unknown,
): data is AssetPerformanceReportData {
  return AssetPerformanceReportData.safeParse(data).success
}

// ============================================================================
// Task Completion Report Data Types
// ============================================================================

/**
 * Monthly completion data for a specific task
 */
export const MonthlyCompletion = z.object({
  // Optional for back-compat: older payloads fall back to `completed`.
  state: z.enum(['completed', 'not_performed', 'not_scheduled']).optional(),
  completed: z.boolean(),
  completedBy: z.string().optional(),
  completedAt: z.string().optional(), // ISO timestamp
})
export type MonthlyCompletion = z.infer<typeof MonthlyCompletion>

/**
 * Unit task completion data
 */
export const UnitTaskCompletion = z.object({
  unitId: z.string(),
  unitName: z.string(),
  siteId: z.string(),
  siteName: z.string(),
  monthlyCompletions: z.record(z.string(), MonthlyCompletion),
})
export type UnitTaskCompletion = z.infer<typeof UnitTaskCompletion>

/**
 * Summary metrics for task completion report
 */
export const TaskCompletionSummary = z.object({
  taskId: z.string(),
  taskName: z.string(),
  taskSection: z.string().optional(),
  totalTasks: z.number(),
  completedTasks: z.number(),
  incompleteTasks: z.number(),
  completionRate: z.number(),
  appliedFilters: AppliedFilters,
})
export type TaskCompletionSummary = z.infer<typeof TaskCompletionSummary>

/**
 * Complete report data structure for task completion reports
 */
export const TaskCompletionReportData = z.object({
  summary: TaskCompletionSummary,
  units: z.array(UnitTaskCompletion),
})
export type TaskCompletionReportData = z.infer<typeof TaskCompletionReportData>

/**
 * Type guard to check if report data is TaskCompletionReportData
 */
export function isTaskCompletionReportData(
  data: unknown,
): data is TaskCompletionReportData {
  return TaskCompletionReportData.safeParse(data).success
}

// ============================================================================
// Maintenance Hours Summary Report Data Types
// ============================================================================

/**
 * Customer (customer organization) hours data
 */
export const CustomerHoursData = z.object({
  customerOrganizationId: z.string().nullable(),
  name: z.string(),
  totalMinutes: z.number(),
  totalHours: z.number(),
  unitCount: z.number(),
  siteCount: z.number(),
})
export type CustomerHoursData = z.infer<typeof CustomerHoursData>

/**
 * Property (site) hours data
 */
export const PropertyHoursData = z.object({
  siteId: z.string(),
  siteName: z.string(),
  customerOrganizationId: z.string().nullable().optional(),
  customerOrganizationName: z.string().optional(),
  totalMinutes: z.number(),
  totalHours: z.number(),
  unitCount: z.number(),
})
export type PropertyHoursData = z.infer<typeof PropertyHoursData>

/**
 * Summary metrics for maintenance hours summary report
 */
export const MaintenanceHoursSummary = z.object({
  totalMinutes: z.number(),
  totalHours: z.number(),
  totalSites: z.number(),
  totalUnits: z.number(),
  totalTasks: z.number(),
  appliedFilters: AppliedFilters,
})
export type MaintenanceHoursSummary = z.infer<typeof MaintenanceHoursSummary>

/**
 * Complete report data structure for maintenance hours summary reports
 */
export const MaintenanceHoursSummaryReportData = z.object({
  summary: MaintenanceHoursSummary,
  customerHours: z.array(CustomerHoursData),
  propertyHours: z.array(PropertyHoursData),
})
export type MaintenanceHoursSummaryReportData = z.infer<
  typeof MaintenanceHoursSummaryReportData
>

/**
 * Type guard to check if report data is MaintenanceHoursSummaryReportData
 */
export function isMaintenanceHoursSummaryReportData(
  data: unknown,
): data is MaintenanceHoursSummaryReportData {
  return MaintenanceHoursSummaryReportData.safeParse(data).success
}

// ============================================================================
// Report Data Discriminated Union
// ============================================================================

/**
 * Template IDs for all supported report types
 */
export const ReportTemplateId = z.enum([
  'maintenance-completion',
  'asset-performance',
  'task-completion',
  'survey-quality',
  'maintenance-hours-summary',
])
export type ReportTemplateId = z.infer<typeof ReportTemplateId>

/**
 * Report templates that may be shared with client users (MCP-699).
 *
 * Deliberately narrow. `maintenance-completion` is the report service companies
 * already print and mail to their customers, which is what client sharing
 * replaces. The others stay internal:
 *  - `maintenance-hours-summary` totals labour hours for contract bidding
 *  - `survey-quality` is a field-ops worksheet for collecting missing data
 *  - `task-completion` and `asset-performance` have not been reviewed for
 *    customer consumption
 *
 * Shared by the frontend (to gate the share control) and the backend (to
 * enforce it) so the two cannot drift.
 */
export const CLIENT_SHAREABLE_TEMPLATE_IDS: readonly ReportTemplateId[] = [
  'maintenance-completion',
]

/**
 * The single customer organization a report belongs to, or null when it is not
 * narrowed to exactly one (MCP-699).
 *
 * This is the whole sharing rule. A report covering several customers, or with
 * no customer filter, has no single audience and therefore cannot be shared:
 * its `customerCompletion[]` enumerates every customer it covers, so one
 * customer would see another's completion rates.
 *
 * Report generation applies `customerOrganizationIds` before any aggregation
 * (see `applySiteLevelFilters`), so a report narrowed to one organization is
 * narrowed in every section of its payload.
 */
export function getSoleCustomerOrganizationId(
  filters: ReportFilters,
): string | null {
  const orgIds = filters.customerOrganizationIds ?? []
  return orgIds.length === 1 ? orgIds[0] : null
}

export function isClientShareableTemplate(
  templateId: ReportTemplateId,
): boolean {
  return CLIENT_SHAREABLE_TEMPLATE_IDS.includes(templateId)
}

/**
 * Discriminated union of all report data types
 * Each report template has its own strongly-typed data structure
 */
export type ReportData =
  | MaintenanceCompletionReportData
  | AssetPerformanceReportData
  | TaskCompletionReportData
  | SurveyQualityReportData
  | MaintenanceHoursSummaryReportData

/**
 * Type guard to check if report data is MaintenanceCompletionReportData
 */
export function isMaintenanceCompletionReportData(
  data: unknown,
): data is MaintenanceCompletionReportData {
  return MaintenanceCompletionReportData.safeParse(data).success
}

// ============================================================================
// Report Filters and Core Types
// ============================================================================

/**
 * Report Filters Schema
 * Defines the filters that can be applied to reports
 */
export const ReportFilters = z.object({
  startDate: z.string().min(1, 'Start date is required'), // ISO date string
  endDate: z.string().min(1, 'End date is required'), // ISO date string

  // Optional filters - specific to report type
  siteIds: z.array(z.string()).optional(),
  customerOrganizationIds: z.array(z.string()).optional(),
  workRouteIds: z.array(z.string()).optional(),
  mechanicIds: z.array(z.string()).optional(),
  taskId: z.string().optional(),
})

export type ReportFilters = z.infer<typeof ReportFilters>

/**
 * Report Status
 */
export const ReportStatus = z.enum(['generating', 'ready', 'failed'])
export type ReportStatus = z.infer<typeof ReportStatus>

/**
 * Report Metadata Schema
 * Contains report-specific metadata and aggregated information
 */
export const ReportMetadata = z.object({
  templateName: z.string(),
  templateVersion: z.string(),
  filterCount: z.number().default(0),

  // Report-specific metrics (populated when status is 'ready')
  totalTasks: z.number().optional(),
  completionRate: z.number().optional(),

  // Timestamps
  generatedAt: FirestoreTimestamp.optional(),
  failedAt: FirestoreTimestamp.optional(),
})

export type ReportMetadata = z.infer<typeof ReportMetadata>

/**
 * Report Schema
 * Main report document structure
 * Collection: /companies/{companyId}/reports/{reportId}
 */

const SharedReportFields = z.object({
  id: z.string().min(1, 'Report ID is required'),
  title: z.string().min(1, 'Report title is required'),

  // Multi-tenant isolation
  serviceCompanyId: z.string().min(1, 'Service company ID is required'),

  // Ownership
  createdBy: z.string().min(1, 'Creator user ID is required'),
  createdAt: FirestoreTimestamp,

  // Report status
  status: ReportStatus,

  // Applied filters
  filters: ReportFilters,

  // Report metadata
  metadata: ReportMetadata,

  // Error message (populated when status is 'failed')
  error: z.string().optional(),

  // Optional: Storage path reference for debugging
  storagePath: z.string().optional(),

  // Optional: Auto-cleanup date
  expiresAt: FirestoreTimestamp.optional().nullable(),

  // Client sharing (MCP-699). A report is invisible to client users until a
  // company user explicitly shares it.
  //
  // No audience is stored: a report is only shareable when its own filters
  // narrow it to exactly one customer organization, so that organization *is*
  // the audience. See `getSoleCustomerOrganizationId`.
  //
  // Reports are shared whole — nothing is stripped — because this is the report
  // service companies already print and mail to these customers.
  //
  // Existing reports predate this field. The client query filters on
  // `sharedWithClient == true`, and Firestore excludes documents missing the
  // field from equality matches, so they stay unshared without a backfill.
  sharedWithClient: z.boolean().default(false),
})

// Generated report data (populated when status is 'ready')
// Structure is strongly typed based on templateId

export const Report = z.discriminatedUnion('templateId', [
  SharedReportFields.extend({
    templateId: z.literal('maintenance-completion'),
    data: MaintenanceCompletionReportData.optional(),
  }),
  SharedReportFields.extend({
    templateId: z.literal('asset-performance'),
    data: AssetPerformanceReportData.optional(),
  }),
  SharedReportFields.extend({
    templateId: z.literal('task-completion'),
    data: TaskCompletionReportData.optional(),
  }),
  SharedReportFields.extend({
    templateId: z.literal('survey-quality'),
    data: SurveyQualityReportData.optional(),
  }),
  SharedReportFields.extend({
    templateId: z.literal('maintenance-hours-summary'),
    data: MaintenanceHoursSummaryReportData.optional(),
  }),
])

export type Report = z.infer<typeof Report>
